// /src/components/InvoicesPage.js
import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";

import CustomInput from "../../components/CustomInput";
import { toMoney } from "../../utils/toMoney";
import { useTranslation } from "react-i18next";
import CustomButton from "../../components/CustomButton";

import classes from "../../components/Modal.module.css";
import { DataContext } from "../../utils/DataContext";
import { UserAuth } from "../../utils/AuthContext";

import InformationBox from "../../components/InformationBox";
import Modal from "../../components/Modal";
import useLanguage from "../../utils/useLanguage";
import Dropdown from "../../components/Dropdown";
import BackToDashboard from "../../components/BackToDashboard";
import Loader from "../../components/Loader";
import { formatDate } from "../../utils/toDate";
import AddItemInvoiceModal from "./AddItemInvoiceModal";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import useRequest from "../../utils/useRequest";
import { sanitizeNumber } from "../../utils/sanitizeNumber";

const ItemInvoices = () => {
  // const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // New state variables for the row modal
  const [selectedRow, setSelectedRow] = useState(null);
  const [showRowModal, setShowRowModal] = useState(false);
  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [invoicesStatus, setInvoicesStatus] = useState({
    title: "Active",
    value: "active",
  });

  // Handler for opening the row info modal
  const handleRowButtonClick = (row) => {
    setSelectedRow(row);
    setShowRowModal(true);
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
  };

  // Handler for closing the row info modal
  const handleCloseRowModal = () => {
    setSelectedRow(null);
    setShowRowModal(false);
  };

  const handlePrint = () => {
    const direction = currentDirection === "rtl" ? "rtl" : "ltr";
    const textAlign = currentDirection === "rtl" ? "right" : "left";

    // Determine color based on the relationship between balanceBeforeUpdate and balanceAfterUpdate
    const amountColor =
      selectedRow?.balanceAfterUpdate < selectedRow?.balanceBeforeUpdate
        ? "red"
        : "green";

    const printContent = `
      <div style="padding: 20px; font-family: Arial, sans-serif;" dir="${direction}">
        <h2 style="text-align: ${textAlign};">${t("BalanceInformation")}</h2>
        
        <table style="width: 100%; text-align: ${textAlign}; border-collapse: collapse;">
          <!-- Supplier Information -->
          <tr>
            <td style="padding: 8px; vertical-align: top;">
              <strong>${t("supplier")}:</strong>
            </td>
            <td style="padding: 8px; vertical-align: top;">
              selectedSupplier?.name || t("No supplier available")}
            </td>
          </tr>
          
          <!-- balanceBeforeUpdate -->
          <tr>
            <td style="padding: 8px; vertical-align: top;">
              <strong>${t("balanceBeforeUpdate")}:</strong>
            </td>
            <td style="padding: 8px; vertical-align: top;">
              ${toMoney(selectedRow?.balanceBeforeUpdate)}
            </td>
          </tr>
          
          <!-- Container's Prices -->
          <tr>
            <td style="padding: 8px; vertical-align: top;">
              <strong>${t("containersPrices")}:</strong>
            </td>
            <td style="padding: 8px; vertical-align: top;">
              <span style="color: ${amountColor};">
                ${toMoney(selectedRow?.amountInSupCur)}
              </span>
            </td>
          </tr>
          
          <!-- balanceAfterUpdate -->
          <tr>
            <td style="padding: 8px; vertical-align: top;">
              <strong>${t("balanceAfterUpdate")}:</strong>
            </td>
            <td style="padding: 8px; vertical-align: top;">
              <span style="color: ${
                selectedRow?.balanceAfterUpdate >= 0 ? "green" : "red"
              };">
                ${toMoney(selectedRow?.balanceAfterUpdate)}
              </span>
            </td>
          </tr>
          
          <!-- Date -->
          <tr>
            <td style="padding: 8px; vertical-align: top;">
              <strong>${t("Date")}:</strong>
            </td>
            <td style="padding: 8px; vertical-align: top;">
              ${
                selectedRow?.modifiedAt
                  ? new Date(selectedRow.modifiedAt).toISOString().split("T")[0]
                  : t("No date available")
              }
            </td>
          </tr>
          
          <!-- Notes -->
          <tr>
            <td style="padding: 8px; vertical-align: top;">
              <strong>${t("notes")}:</strong>
            </td>
            <td style="padding: 8px; vertical-align: top;">
              ${selectedRow?.notes || t("No notes available")}
            </td>
          </tr>
        </table>
      </div>
    `;

    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(`
      <html dir="${direction}">
      <head>
        <title>${t("Print")}</title>
        <style>
          body { font-family: Arial, sans-serif; direction: ${direction}; }
          h2 { color: #333; text-align: ${textAlign}; }
          table { width: 100%; border-collapse: collapse; }
          td { padding: 8px; vertical-align: top; }
          strong { color: #555; }
        </style>
      </head>
      <body>
        ${printContent}
      </body>
      </html>
    `);
    newWindow.document.close();
    newWindow.print();
  };

  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  const [chartModalOpen, setChartModalOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState(null);
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("invoiceNumber"),
    value: "invoiceNumber",
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [balances, setBalances] = useState({
    finalAmount: 0,
    receivedAmount: 0,
    leftAmount: 0,
  });

  const { userData } = useContext(UserAuth);
  const { customers, getCustomers } = useContext(DataContext);

  const {
    data,
    loading,
    error,
    refetch: refetchItemInvoices,
  } = useRequest(
    `/getAllItemInvoice`,
    "GET",
    null,
    {
      userId: userData?.userId,
      organizationId: userData?.organizationId,
    },
    false
  );

  // * Reset to default
  useEffect(() => {
    return () => {
      setInvoicesStatus({
        title: "Active",
        value: "active",
      });
    };
  }, []);

  const tableTitles = [
    {
      value: "invoiceNumber",
    },
    {
      value: "customerName",
    },
    {
      value: "totalAmount",
    },
    {
      value: "invoiceType",
    },
    {
      value: "invoiceDate",
    },
    {
      value: "createdBy",
    },
  ];

  const tableRows = data?.data?.map((invoice) => [
    {
      value: invoice?.id,
      type: TABLE_CELL_TYPES.LINK,
      to: `/itemInvoice/${invoice.id}`,
      noWrapText: true,
    },
    {
      value: invoice?.customer?.name,
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: invoice?.finalAmount,
      type: TABLE_CELL_TYPES.MONEY,
    },
    {
      value: t(invoice?.invoiceType),
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: new Date(invoice?.date).toISOString().split("T")[0],
      type: TABLE_CELL_TYPES.DATE,
    },
    {
      value: invoice?.user?.username,
      type: TABLE_CELL_TYPES.TEXT,
    },
  ]);

  const balanceStyle = "positive-value";

  // Fetch the data
  useEffect(() => {
    refetchItemInvoices({
      page: pagination.currentPage,
      limit: 5,
      status: invoicesStatus.value,
    });
  }, [pagination.currentPage]);

  // Set the selected customer, and Pagination
  useEffect(() => {
    if (data) {
      const pagination = data?.pagination;
      setPagination({
        currentPage: pagination?.page,
        totalPages: pagination?.pages,
        totalRecords: pagination?.total,
      });

      setBalances(data?.totals);
    }
  }, [data]);

  const handleChangeStatus = async (status) => {
    const { value } = status;
    console.log("status", status);
    setInvoicesStatus(status);
    setSearchQuery("");

    await refetchItemInvoices({
      status: value,
    });
  };

  const handleShowChart = () => {
    setChartModalOpen(true);
  };

  const handleCloseChart = () => {
    setChartModalOpen(false);
  };

  const handleSearchType = async (type) => {
    if (type.value === "invoiceNumber") {
      setSelectedDate(new Date());
    } else if (type.value === "finalAmount") {
      setSelectedDate(new Date());
    } else if (type.value === "date") {
      setSearchQuery("");
    } else if (type.value === "customer") {
      await getCustomers();
      setSelectedDate(new Date());
    } else {
      throw error("invalid search type");
    }

    setSelectedTitle(type);
  };

  const handleChangeCustomer = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleSearch = async (query = searchQuery) => {
    if (typeof query !== "string") {
      query = searchQuery;
    }

    let searchBy;
    if (selectedTitle.value === "date") {
      searchBy = selectedDate;
    } else if (selectedTitle.value === "finalAmount") {
      searchBy = sanitizeNumber(query);
    } else if (selectedTitle.value === "customer") {
      searchBy = selectedCustomer?.id;
    } else {
      searchBy = query;
    }

    if (invoicesStatus.value === "active") {
      await refetchItemInvoices({
        searchType: selectedTitle.value,
        searchQuery: searchBy,
      });
    } else {
      await refetchItemInvoices({
        searchType: selectedTitle.value,
        searchQuery: searchBy,
        status: invoicesStatus.value,
      });
    }
  };

  // show the add invoice modal
  const handleToggleModal = () => {
    setShowAddInvoiceModal(() => !showAddInvoiceModal);
  };

  return (
    <>
      <main className="e-container">

        <div className="invoice-table-container">

          <div className="under-user-section">
            <div className="page-title">{t("salesInvoices")}</div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              <>
                <div className="top-action-buttons">
                  <div className="button-group">
                    <CustomButton
                      title={t("createInvoice")}
                      icon="add"
                      iconSize={20}
                      elementType={Link}
                      // to="/createInvoiceOfMultiContsPage"
                      onClick={handleToggleModal}
                    />
                  </div>
                  <section className="col title-col ">
                    <label>{t("invoicesStatus")}: </label>
                    <Dropdown
                      options={[
                        { title: "Active", value: "active" },
                        { title: "Archived", value: "archived" },
                      ]}
                      selected={invoicesStatus}
                      setSelected={handleChangeStatus}
                      thick
                    />
                  </section>
                </div>

                <>
              <div className="c-search-bar u-margin--bottom_500">
                <section className="c-search-bar-label-input">


                    <label>{t("search")}: </label>
                    {selectedTitle.value === "date" ? (
                      <CustomInput
                        name="date"
                        type="date"
                        onChange={(date) => {
                          setSelectedDate(date?.target?.value);
                        }}
                        value={formatDate(selectedDate)}
                      />
                    ) : selectedTitle.value === "customer" ? (
                      <>
                        {customers?.length ? (
                          <Dropdown
                            options={customers}
                            selected={selectedCustomer}
                            setSelected={handleChangeCustomer}
                            thick
                          />
                        ) : (
                          <span className="error-text">
                            {t("noCustomersFound")}
                          </span>
                        )}
                      </>
                    ) : (
                      <CustomInput
                        placeholder={t("search")}
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                        }}
                        resetSearch={() => {
                          setSearchQuery("");
                          handleSearch("");
                        }}
                        thick
                      />
                    )}
                    </section>
                <section className="c-search-bar-label-input">
                      <label>{t("searchTitle")}: </label>
                      <Dropdown
                        options={[
                          {
                            title: t("invoiceNumber"),
                            value: "invoiceNumber",
                          },
                          { title: t("Date"), value: "date" },
                          { title: t("customer"), value: "customer" },
                          { title: t("totalAmount"), value: "finalAmount" },
                        ]}
                        selected={selectedTitle}
                        setSelected={handleSearchType}
                        thick
                      />
                    </section>

                      <CustomButton
                        title={t("search")}
                        onClick={handleSearch}
                        icon="search"
                        iconSize={15}
                      />
                  </div>
                  {/* <div className="u-width_fit-content"> */}

                  {data?.data?.length !== 0 ? (
                    <RoundedTable
                      titles={tableTitles}
                      rows={tableRows}
                      pagination={pagination}
                      setPagination={setPagination}
                      // withCheckbox
                    />
                  ) : (
                    <InformationBox>{t("noTransactions")}</InformationBox>
                  )}

                  {selectedCustomer?.id ? (
                    <div className="balances o-column o-column--vertical-align_end o-column--gap-size_400 u-margin--top_400">
                      <p className="balance">
                        <span>{t("customerCurrentBalance")}: </span>
                        <span className={balanceStyle}>
                          {toMoney(balances?.finalAmount)}
                        </span>
                      </p>

                      <p className="balance">
                        <span>{t("totalLeftAmountWithCustomer")}: </span>
                        <span className={balanceStyle}>
                          {toMoney(balances?.leftAmount)}
                        </span>
                      </p>
                    </div>
                  ) : null}
                </>
              </>
            </>
          )}
        </div>
      </main>

      {/* Render the Printing modal */}
      {showRowModal && selectedRow && (
        <Modal
          title={t("BalanceInformation")}
          onClose={handleCloseRowModal}
          footer={
            <section className={classes.actions}>
              {selectedRow.invoiceNumber ? null : (
                <CustomButton
                  variant="blank"
                  elementType={Link}
                  title={t("edit")}
                  to={`/editContainer/${selectedRow?.id}`}
                  // onClick={handleEdit}
                  state={selectedRow}
                  icon="edit"
                  iconSize={18}
                />
              )}
              <CustomButton
                title={t("print")}
                onClick={handlePrint}
                variant="gray"
                icon="print"
                iconSize={18}
              />
            </section>
          }
        >
          <div className="">
            {selectedRow?.theDateOfUpdate ? (
              <span className={classes.infoUpdatedIn}>
                {t("infoUpdatedIn")}:
                {" " +
                  new Date(selectedRow?.theDateOfUpdate)
                    .toISOString()
                    .split("T")[0]}
              </span>
            ) : null}

            {/* Supplier Information */}
            <div className="view-user-entity">
              <p>{t("supplier")}:</p>
              <span>{t("No supplier available")}</span>
            </div>

            {/* Balance Before Update */}
            <div className="view-user-entity">
              <p>{t("invoiceNumber")}:</p>
              <span>{toMoney(selectedRow?.balanceBeforeUpdate)}</span>
            </div>

            {/* Container's Prices */}
            <div className="view-user-entity">
              <p>{t("containersPrices")}:</p>
              <span>{toMoney(selectedRow?.amountInSupCur)}</span>
            </div>

            {/*  Balance After Update  */}
            <div className="view-user-entity">
              <p>{t("balanceAfterUpdate")}:</p>
              <span>{toMoney(selectedRow?.balanceAfterUpdate)}</span>
            </div>

            {/* Date */}
            <div className="view-user-entity">
              <p>{t("Date")}:</p>
              <span>
                {" "}
                {selectedRow?.date
                  ? new Date(selectedRow.date).toISOString().split("T")[0]
                  : selectedRow?.modifiedAt
                  ? new Date(selectedRow.modifiedAt).toISOString().split("T")[0]
                  : t("No date available")}
              </span>
            </div>

            {/* Notes */}
            <div className="view-user-entity">
              <p>{t("notes")}:</p>
              <span>{selectedRow?.notes || t("No notes available")}</span>
            </div>
          </div>
        </Modal>
      )}

      {/* ADD INVOICE MODAL  */}
      {showAddInvoiceModal && (
        <AddItemInvoiceModal
          handleToggleModal={handleToggleModal}
          refetchItemInvoices={refetchItemInvoices}
          mode="create"
        />
      )}
    </>
  );
};

export default ItemInvoices;
